
import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Company } from '../../types';
import { firestoreService } from '../../services/firestoreService';
import Spinner from '../../components/common/Spinner';
import { AuthContext } from '../../contexts/AuthContext';

const VideoIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor"><path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 001.553.832l3-2a1 1 0 000-1.664l-3-2z" /></svg>;
const UploadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
    </svg>
);

const VideosPage: React.FC = () => {
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState(true);
    const authContext = useContext(AuthContext);

    useEffect(() => {
        const fetchCompanies = async () => {
            setLoading(true);
            // Fixed: Added tenantId to getCompanies call
            const tenantId = authContext?.user?.tenantId;
            if (tenantId) {
                const fetchedCompanies = await firestoreService.getCompanies(tenantId);
                setCompanies(fetchedCompanies);
            }
            setLoading(false);
        };
        fetchCompanies();
    }, [authContext?.user?.tenantId]);

    if (loading) {
        return <div className="flex justify-center items-center h-full"><Spinner size="lg" /></div>;
    }

    return (
        <div className="container mx-auto">
             <div className="flex justify-between items-center mb-6">
                 <h1 className="text-3xl font-bold text-white">Manage Videos by Company</h1>
                 <Link 
                    to="/videos/new"
                    className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center gap-2"
                >
                    <UploadIcon />
                    Upload Video
                </Link>
             </div>
            <p className="text-gray-400 mb-8">Select a company to view and manage its videos. You can upload new videos at any time.</p>

            <div className="bg-gray-900 rounded-lg shadow-lg overflow-hidden">
                <table className="min-w-full">
                    <thead className="bg-gray-800/50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Company Name</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                        {companies.map((company) => (
                            <tr key={company.companyId} className="hover:bg-gray-800/40 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white flex items-center">
                                    {company.settings?.branding?.logoUrl ? (
                                        <img src={company.settings.branding.logoUrl} alt={company.name} className="h-8 w-auto mr-4"/>
                                    ) : (
                                        <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center font-bold text-sm mr-4" style={{backgroundColor: company.settings?.branding?.backgroundColor}}>
                                          {company.name.charAt(0)}
                                        </div>
                                    )}
                                    {company.name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <Link 
                                        to={`/videos/company/${company.companyId}`} 
                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-blue-500"
                                    >
                                        <VideoIcon />
                                        View Videos
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default VideosPage;
